import { useState } from 'react';
import type { Consent } from '@repo/domain';
import { useConsentActions } from '@repo/domain-service';

import { useAuthStore } from '@features/auth';

import { useReservationConsentStore } from '@shared/stores/useReservationConsentStore';
import { isReservationConsentCheckFresh } from '@shared/utils/isReservationConsentCheckFresh';

interface ReservationConsentGateResult {
  expectedConsents: Consent[];
  checkingConsents: boolean;
  acceptingConsents: boolean;
  checkConsents: () => Promise<boolean>;
  acceptConsents: () => Promise<void>;
}

/**
 * Asks for the reservation consents after the vehicle is chosen and before it is
 * assigned to the reservation. `checkConsents` resolves `true` when the user may
 * go on straight away; otherwise it exposes the consents to ask for. A fresh
 * check from the store is trusted as is; without one the consents are fetched.
 * The consent is not essential enough to block a reservation, so a failed fetch
 * or a failed acceptance lets the user through.
 */
export function useReservationConsentGate(): ReservationConsentGateResult {
  const ownerId = useAuthStore((state) => state.user?.id);
  const saveCheck = useReservationConsentStore((state) => state.saveCheck);
  const { getConsents, acceptConsent, savingConsents } = useConsentActions();
  const [expectedConsents, setExpectedConsents] = useState<Consent[]>([]);
  const [checkingConsents, setCheckingConsents] = useState<boolean>(false);

  const resolveExpectedConsents = async (): Promise<Consent[]> => {
    const check = useReservationConsentStore.getState().check;
    if (isReservationConsentCheckFresh(check, ownerId, Date.now())) return check.expectedConsents;

    try {
      const consents = await getConsents({ context: 'reservation' });
      saveCheck(ownerId, consents);
      return consents.filter((consent) => consent.isExpected);
    } catch {
      return [];
    }
  };

  const checkConsents = async (): Promise<boolean> => {
    setCheckingConsents(true);
    const expected = await resolveExpectedConsents();
    setCheckingConsents(false);
    setExpectedConsents(expected);
    return expected.length === 0;
  };

  const acceptConsents = async (): Promise<void> => {
    const results = await Promise.allSettled(
      expectedConsents.map((consent) =>
        acceptConsent({ context: 'reservation', type: consent.type, version: consent.currentVersion })
      )
    );

    // Only a full success is remembered - otherwise the next reservation asks again.
    if (results.every((result) => result.status === 'fulfilled')) {
      saveCheck(ownerId, []);
    }

    setExpectedConsents([]);
  };

  return {
    expectedConsents,
    checkingConsents,
    acceptingConsents: savingConsents,
    checkConsents,
    acceptConsents,
  };
}
